import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import {
  Container,
  Text,
  TextAreaFormikField,
  TagGroupSelectFormikField,
  Popover,
  DialogTrigger,
  LinkButton,
  PopoverContent,
  TextInputFormikField,
  Toggle,
  ToggleField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Formik } from "formik";
import { useContext } from "react";
import { useTranslation, Trans } from "react-i18next";
import { array, boolean, object, string } from "yup";

import {
  applyPackageChannelToggle,
  PackageChannelFlag,
} from "../../package-utils";
import { PackageContext } from "../../PackageContext";
import { PackageStepLayout } from "../PackageStepLayout";
import { useClientAccount } from "../../../../../hooks/useClientAccount";

export function PackageDetailsStep() {
  const { t } = useTranslation();
  const { packageData, setPackage, goToNextStep } = useContext(PackageContext);
  const { client } = useClientAccount();
  const { data: tags } = useClientPortalAPI(
    `/api/internal/client-portal/tags/active/`
  );
  const packageDetailsPageSchema = object().shape({
    name: string().required().label(t("Package name")),
    description: string(),
    tag_names: array(),
    is_ats_package: boolean().default(false),
    is_api_package: boolean().default(false),
    ...(client?.applicant_pay_enabled && {
      is_applicant_pay: boolean().default(false),
    }),
  });

  // This combines the existing tags from the api and what is in the package context
  // then makes a unique list so that the step backward works
  const tagsList = tags?.concat(packageData.tag_names);
  const uniqueTagsMap = new Map(tagsList?.map((tag) => [tag.id, tag]));
  const uniqueTagsList = [...uniqueTagsMap.values()];

  return (
    <Formik
      initialValues={packageData}
      onSubmit={(values) => {
        setPackage({ ...packageData, ...values });
        goToNextStep();
      }}
      validationSchema={packageDetailsPageSchema}
    >
      {({ handleSubmit, isSubmitting, isValid, values, setFieldValue }) => {
        const handleChannelToggle = (
          channel: PackageChannelFlag,
          selected: boolean
        ) => {
          Object.entries(applyPackageChannelToggle(channel, selected)).forEach(
            ([field, value]) => setFieldValue(field, value)
          );
        };

        return (
          <PackageStepLayout
            onComplete={handleSubmit}
            disableCompletion={!isValid}
            submitting={isSubmitting}
          >
            <Container
              layout="narrow"
              testID="package-details-page-outermost-container"
            >
              <Text
                as="h1"
                className={styles.titleSpacing}
                style="global/title-small"
              >
                {t("Create new package")}
              </Text>
              <div className={styles.mainContentContainer}>
                <section>
                  <TextInputFormikField
                    name="name"
                    label={t("Package name")}
                    testID="package-name-input"
                    isRequired
                  />
                </section>
                <section>
                  <TextAreaFormikField
                    label={t("Package description")}
                    name="description"
                    testID="package-description-input"
                    maxLength={120}
                    description={t(
                      "This will only be seen by your team and is not viewable to the applicant."
                    )}
                  />
                </section>
                <section className={styles.packageTypeToggles}>
                  <Text style="form/body-secondary">
                    {t("Ordering through an ATS or the API? Choose one.")}
                  </Text>
                  <Toggle
                    testID="ats-package-toggle"
                    isSelected={Boolean(values.is_ats_package)}
                    onChange={(selected) =>
                      handleChannelToggle("is_ats_package", selected)
                    }
                    description={t(
                      "Orders come from a connected ATS integration."
                    )}
                  >
                    {t("Mark this package for ATS use")}
                  </Toggle>
                  <Toggle
                    testID="api-package-toggle"
                    isSelected={Boolean(values.is_api_package)}
                    onChange={(selected) =>
                      handleChannelToggle("is_api_package", selected)
                    }
                    description={t(
                      "Orders come through the Certn API. Settings saved here fill in anything the order doesn't send."
                    )}
                  >
                    {t("Mark this package for API use")}
                  </Toggle>
                </section>
                {client?.applicant_pay_enabled && (
                  <section>
                    <div className={styles.toggleWithInfo}>
                      <ToggleField
                        label={t("Eligible for applicant pay")}
                        name="is_applicant_pay"
                        testID="applicant-pay-toggle"
                      >
                        {t("Eligible for applicant pay")}
                      </ToggleField>
                      <DialogTrigger>
                        <LinkButton
                          className={styles.toggleWithInfoButton}
                          testID="applicant-pay-whats-this-button"
                        >
                          {t("What's this?")}
                        </LinkButton>
                        <Popover placement="top right">
                          <PopoverContent>
                            {t(
                              "When a package is created with this field enabled, the checks included in the package are eligible for applicant pay."
                            )}
                          </PopoverContent>
                        </Popover>
                      </DialogTrigger>
                    </div>
                  </section>
                )}
                <section>
                  {tags && (
                    <TagGroupSelectFormikField
                      label={t("Tags")}
                      name="tag_names"
                      testID="tag-select-field"
                      values={uniqueTagsList || []}
                      selectedValues={packageData.tag_names}
                      setSelectedValues={() => undefined}
                      description={t(
                        "Tags applied to this package will be automatically be added to each case."
                      )}
                      info={
                        <DialogTrigger>
                          <LinkButton testID="tag-group-whats-this-button">
                            {t("What's this?")}
                          </LinkButton>
                          <Popover placement="top right">
                            <PopoverContent>
                              <Trans>
                                Tags allow you to categorize and identify themes
                                in your orders. Tags can be managed within the
                                group settings and they will be visible to all
                                members within the group. Applicants will not
                                see this.
                              </Trans>
                            </PopoverContent>
                          </Popover>
                        </DialogTrigger>
                      }
                    />
                  )}
                </section>
              </div>
            </Container>
          </PackageStepLayout>
        );
      }}
    </Formik>
  );
}

const styles = {
  titleSpacing: css({ marginBottom: "var(--space-1)" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
  }),
  packageTypeToggles: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
  }),
  toggleWithInfo: css({
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
  }),
  toggleWithInfoButton: css({
    flexShrink: "0",
  }),
};
